import Vue from 'vue'
import Vuex from 'vuex'
Vue.use(Vuex)

import cookies from '../../plugins/cookies'
import state from './state'
import getters from './getters'
import mutations from './mutations'
import actions from './actions'

// Register any store modules that are required to start the app
// Dynamically loaded modules should be loaded in their root components
const store = new Vuex.Store({
    getters,
    mutations,
    state,
    actions,
    modules: [],
    plugins: [],
    strict: process.env.NODE_ENV !== 'production'
})

const apply_reductions = (reducer, ignored_mutations, mutation, state) => {
    if (ignored_mutations.includes(mutation)) {
        return false
    }
    if (!reducer) {
        return state
    }
    var reduced_state = reducer(mutation, state)

    if (reduced_state !== false && typeof reduced_state !== 'object') {
        throw `reducer return type: ${typeof reduced}. Must be a bool or object`
    }

    return reduced_state
}

const runHook = function (hook) {
    if (!hook) {
        return Promise.resolve()
    }

    var hookType = typeof hook
    if (hookType === 'string') {
        // call the associated action
        return store.dispatch(hook)
    }

    if (hookType === 'function') {
        var result = hook()
        if (result.then) {
            return result
        }
        return Promise.resolve(result)
    }
    throw 'Auth hooks MUST be either an action name or a function themselves'
}

const applyAuthHooks = function (auth, name) {
    if (!auth) {
        return
    }
    var hooks = auth.hooks || {}

    store.watch(
        (state, getters) => getters['auth/token'],
        async function (newToken, oldToken) {
            if (oldToken === undefined || newToken == oldToken) {
                // store initializing, no actual change yet
                return
            }

            // user is logging out (token = something to token = null)
            if (!newToken) {
                var response = await runHook(hooks.logout, name)

                if (auth.require && name !== 'dashboard') {
                    store.commit(`${name}/CLEAR`)
                }
                return response
            }

            // token is being refreshed (token = something to token = something)
            if (oldToken) {
                return await runHook(hooks.refresh, name)
            }

            // user is logging in (token = null to token = something)
            return await runHook(hooks.login, name)
        }
    )
}

const injectSavedStateToSubModules = function (mods, parent_state) {
    if (!mods) {
        return
    }
    Object.keys(mods).forEach((mod_name) => {
        var mod = mods[mod_name]
        var new_mod_state = mod.state()
        var saved_mod_state = parent_state[mod_name]
        delete parent_state[mod_name]
        mod.state = () => Object.assign({}, new_mod_state, saved_mod_state)
        injectSavedStateToSubModules(mod.modules, saved_mod_state)
    })
}

store.unregister = (sub_module) => {
    if (typeof sub_module === 'object' && sub_module.name) {
        sub_module = sub_module.name
    }
    store.unregisterModule(sub_module)
}

store.register = ({
    name,
    getters,
    mutations,
    actions,
    state,
    modules,
    auth,
    persistence,
    watchers
}) => {
    if (store.hasModule(name)) {
        return
    }

    var load = () => {}
    var save = () => {}
    var subscribeMutations = () => {}
    var module_prefix = `${name}/`
    getters = getters == null ? {} : getters
    actions = actions == null ? {} : actions
    mutations = mutations == null ? {} : mutations
    modules = modules == null ? {} : modules
    if (!name) {
        throw 'registered store modules MUST have a name'
    }

    if (!state) {
        state = () => {
            return {}
        }
    }
    if (typeof state === 'object') {
        const default_state = state
        state = () => default_state
    }
    if (typeof state !== 'function') {
        throw 'state must be a function which returns the default state structure or the default state object'
    }

    var default_state = state()
    var saved_state = {}

    if (typeof default_state !== 'object') {
        throw 'state() must return a json object'
    }

    if (persistence) {
        switch (persistence.type) {
            case 'localStorage':
                load = () => {
                    return JSON.parse(localStorage.getItem(name))
                }
                save = (mutation, state) => {
                    localStorage.setItem(name, JSON.stringify(state))
                }
                break
            case 'sessionStorage':
                load = () => {
                    return JSON.parse(sessionStorage.getItem(name))
                }
                save = (mutation, state) => {
                    sessionStorage.setItem(name, JSON.stringify(state))
                }
                break
            case 'cookie':
                load = () => {
                    return JSON.parse(cookies.get(name))
                }
                save = (mutation, state) => {
                    cookies.set({
                        key: name,
                        value: JSON.stringify(state)
                    })
                }
                break
            case 'zutak':
                load = async () => {
                    try {
                        await document.promisedEvent('zutakReady')
                        var data = await Zutak.storage.readFile(`${name}_vuex.json`)
                        return JSON.parse(data)
                    } catch (error) {
                        //file doesn't exist
                        return {}
                    }
                }
                save = async (mutation, state) => {
                    await Zutak.storage.saveFile(`${name}_vuex.json`, state, true)
                }
                break
        }

        if ('load' in persistence) {
            load = persistence['load']
        }
        if ('save' in persistence) {
            save = persistence['save']
        }

        if (typeof save !== 'function') {
            throw `save() must be a function (module: ${name})`
        }
        if (typeof load !== 'function') {
            throw `load() must be a function (module: ${name})`
        }

        saved_state = load() || {}

        var reducer = persistence.reducer
        if (!reducer) {
            reducer = (mutation, state) => state
        }
        if (typeof reducer !== 'function') {
            throw 'reducer must be a function which returns either a bool or a potentially reduced form of the state'
        }
        var ignored_mutations = ['_INITIALIZE', ...(persistence.ignored_mutations || [])]

        subscribeMutations = () => {
            store.subscribe((mutation, state) => {
                if (mutation.type.startsWith(module_prefix)) {
                    var module_mutation = mutation.type.replace(module_prefix, '')
                    var reduced_state = apply_reductions(
                        reducer,
                        ignored_mutations,
                        module_mutation,
                        state[name]
                    )
                    if (reduced_state) {
                        save(module_mutation, reduced_state)
                    }
                }
            })
        }
    }

    const new_mutations = {
        CLEAR: (vuex_state) => {
            Object.assign(vuex_state, state())
        },
        _INITIALIZE(state, new_state) {
            Object.assign(state, new_state)
        },
        ...mutations
    }

    var new_state = {
        ...default_state
    }

    if (!saved_state.then) {
        // load is async promise
        Object.assign(new_state, saved_state)
        injectSavedStateToSubModules(modules, new_state)
    } else {
        var asyncLoad = async (state, commit) => {
            commit(`${name}/_INITIALIZE`, await saved_state)
        }
    }

    const new_module = {
        namespaced: true,
        state: new_state,
        mutations: new_mutations,
        getters,
        actions,
        modules
    }

    store.registerModule(name, new_module)

    if (watchers) {
        Object.keys(watchers).forEach((prop) => {
            if (!watchers[prop].handler || typeof watchers[prop].handler !== 'function') {
                throw `${prop} watcher needs a defined handler function.`
            }
            var attrs = { ...watchers[prop] }
            var accessor = (state, getters) => getters[prop]
            delete attrs.handler

            const handler = (current, old) => {
                watchers[prop].handler(store, current, old)
            }
            store.watch(accessor, handler, attrs)
        })
    }

    var init = Promise.resolve()

    if (asyncLoad) {
        init = init.then(() => asyncLoad(new_module.state, store.commit))
    }

    if (new_module.actions.initialize) {
        init = init.then(() => store.dispatch(`${name}/initialize`))
    }

    return init.then(async () => {
        subscribeMutations()
        applyAuthHooks(auth, name)
    })
}

export default store
